/**
 * INJECT SALDO MANAGER v2.0 - Menu untuk Manager/Owner
 * Fitur untuk approve request dan direct inject saldo dengan kategori
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Wallet,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Plus,
  History,
  FileText,
  DollarSign,
  Upload,
  X,
  AlertTriangle,
  Monitor,
  Edit3
} from 'lucide-react';

const InjectSaldoManager = ({ api, currentUser }) => {
  // Only OWNER can see/reuse REVISI categories
  const isOwner = currentUser?.role === 'OWNER';
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // KATEGORI options
  const allKategoriOptions = [
    {
      value: 'SALDO_MAU_ABIS',
      label: 'Saldo Mau Habis',
      icon: '⚠️',
      color: 'yellow',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-400'
    },
    {
      value: 'SALDO_HABIS',
      label: 'Saldo Habis',
      icon: '❌',
      color: 'red',
      bgColor: 'bg-red-100',
      textColor: 'text-red-700',
      borderColor: 'border-red-400'
    },
    {
      value: 'REVISI_SISTEM',
      label: 'Revisi Sistem',
      icon: '🖥️',
      color: 'blue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-400'
    },
    {
      value: 'REVISI_INPUT',
      label: 'Revisi Input',
      icon: '✏️',
      color: 'purple',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-400'
    }
  ];

  // Hide REVISI_SISTEM & REVISI_INPUT for MANAGER (OWNER only)
  const kategoriOptions = isOwner
    ? allKategoriOptions
    : allKategoriOptions.filter(k => k.value !== 'REVISI_SISTEM' && k.value !== 'REVISI_INPUT');

  // Data
  const [pendingRequests, setPendingRequests] = useState([]);
  const [injectHistory, setInjectHistory] = useState([]);
  const [lokets, setLokets] = useState([]);

  // Filters
  const [filterKategori, setFilterKategori] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [filterLoket, setFilterLoket] = useState('');

  // Direct inject form
  const [directInjectForm, setDirectInjectForm] = useState({
    loket_id: '',
    bank_code: 'BRIS',
    jumlah_inject: '',
    kategori: '',
    alasan: '',
    keterangan_tambahan: ''
  });
  const [directFilePreview, setDirectFilePreview] = useState(null);
  const directFileInputRef = useRef(null);

  // Approve modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveAmount, setApproveAmount] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const [approveBuktiPreview, setApproveBuktiPreview] = useState(null);

  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'pending') {
        const requests = await api.get('/api/inject-saldo/requests?status=PENDING&limit=50');
        setPendingRequests(requests.data || []);
      } else if (activeTab === 'history') {
        let url = '/api/inject-saldo/history?limit=100';
        if (filterKategori) url += `&kategori=${filterKategori}`;
        if (filterBank) url += `&bank_code=${filterBank}`;
        const history = await api.get(url);
        setInjectHistory(history.data || []);
      }

      // Load lokets for direct inject
      const loketsData = await api.get('/api/inject-saldo/lokets');
      setLokets(loketsData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const getSaldoLoket = (loketId, bankCode) => {
    const loket = lokets.find(l => l.id === loketId);
    if (!loket) return 0;
    const bank = loket.banks?.find(b => b.bank_code === bankCode);
    return bank?.saldo || 0;
  };

  const getKategoriInfo = (kategori) => {
    return kategoriOptions.find(k => k.value === kategori) || {
      value: kategori,
      label: kategori,
      icon: '❓',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700'
    };
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setSubmitting(true);
    setError('');

    try {
      await api.put(`/api/inject-saldo/requests/${selectedRequest.id}/approve`, {
        jumlah_approve: parseFloat(approveAmount || selectedRequest.jumlah_request),
        kategori: selectedRequest.kategori,
        keterangan_tambahan: approveNote,
        bukti_inject: approveBuktiPreview
      });

      setSuccess(`Inject saldo berhasil disetujui!`);
      closeModals();

      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Gagal approve request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectNote.trim()) {
      setError('Alasan penolakan WAJIB diisi');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await api.put(`/api/inject-saldo/requests/${selectedRequest.id}/reject`, {
        rejection_note: rejectNote
      });

      setSuccess('Request ditolak');
      closeModals();

      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Gagal tolak request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDirectInject = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        loket_id: directInjectForm.loket_id,
        bank_code: directInjectForm.bank_code,
        jumlah_inject: parseFloat(directInjectForm.jumlah_inject),
        kategori: directInjectForm.kategori,
        alasan: directInjectForm.alasan,
        keterangan_tambahan: directInjectForm.keterangan_tambahan,
        bukti_inject: directFilePreview
      };

      await api.post('/api/inject-saldo/direct', payload);

      setSuccess('Inject saldo berhasil!');
      setDirectInjectForm({
        loket_id: '',
        bank_code: 'BRIS',
        jumlah_inject: '',
        kategori: '',
        alasan: '',
        keterangan_tambahan: ''
      });
      setDirectFilePreview(null);

      await loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Gagal inject saldo');
    } finally {
      setSubmitting(false);
    }
  };

  const openApproveModal = (request) => {
    setSelectedRequest(request);
    setApproveAmount(request.jumlah_request.toString());
    setApproveNote('');
    setApproveBuktiPreview(null);
    setShowApproveModal(true);
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectNote('');
    setShowRejectModal(true);
  };

  const closeModals = () => {
    setShowApproveModal(false);
    setShowRejectModal(false);
    setSelectedRequest(null);
    setApproveAmount('');
    setApproveNote('');
    setRejectNote('');
  };

  const formatRupiah = (num) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num || 0);
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff} detik lalu`;
    if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
    return new Date(dateString).toLocaleDateString('id-ID');
  };

  const handleDirectFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDirectFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredHistory = injectHistory.filter(item => {
    if (filterLoket && item.loket_id !== filterLoket) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="text-purple-600" />
            Inject Saldo
          </h1>
          <p className="text-gray-500 mt-1">
            Kelola request dan inject saldo untuk semua loket
          </p>
        </div>
        <button
          onClick={loadData}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          title="Refresh"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded flex items-center">
          <CheckCircle className="text-green-500 mr-2" size={20} />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-center">
          <AlertCircle className="text-red-500 mr-2" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'pending'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileText size={18} />
              Request Pending
              {pendingRequests.length > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingRequests.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'history'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <History size={18} />
              Riwayat Inject
            </div>
          </button>
          <button
            onClick={() => setActiveTab('direct')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'direct'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Plus size={18} />
              Inject Langsung
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin mr-2" />
              <span>Memuat data...</span>
            </div>
          ) : (
            <>
              {/* PENDING TAB */}
              {activeTab === 'pending' && (
                <div>
                  {pendingRequests.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="mx-auto mb-2 text-green-400" size={48} />
                      <p className="text-lg">Tidak ada request pending</p>
                      <p className="text-sm">Semua request sudah diproses</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingRequests.map(req => {
                        const kat = getKategoriInfo(req.kategori);
                        return (
                          <div
                            key={req.id}
                            className={`border-2 rounded-lg p-4 hover:bg-gray-50 transition-colors ${kat.borderColor}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                {/* Kategori Badge */}
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${kat.bgColor} ${kat.textColor}`}>
                                    {kat.icon} {kat.label}
                                  </span>
                                  <span className="text-gray-400">|</span>
                                  <span className="font-medium">{req.bank_code === 'BRIS' ? '💚 BSI' : '💙 Mandiri'}</span>
                                  <span className="text-gray-400">|</span>
                                  <span className="text-sm text-gray-600">{req.loket_nama}</span>
                                </div>

                                {/* Amount */}
                                <p className="text-2xl font-bold text-gray-900">
                                  {formatRupiah(req.jumlah_request)}
                                </p>

                                {/* Alasan */}
                                <p className="text-sm text-gray-600 mt-2">{req.alasan}</p>

                                {/* Kategori-specific info */}
                                {req.kategori === 'SALDO_MAU_ABIS' && req.estimasi_trx_tersisa && (
                                  <p className="text-xs text-yellow-600 mt-1">
                                    📊 Estimasi trx tersisa: {req.estimasi_trx_tersisa}
                                  </p>
                                )}
                                {req.kategori === 'REVISI_SISTEM' && (req.error_code || req.ticket_id) && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    🎫 {req.error_code || req.ticket_id}
                                  </p>
                                )}
                                {req.kategori === 'REVISI_INPUT' && req.slip_id && (
                                  <p className="text-xs text-purple-600 mt-1">
                                    📄 Slip: {req.slip_id}
                                  </p>
                                )}

                                {/* Bukti indicator */}
                                {req.bukti && (
                                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                    📎 Bukti: {req.nama_file_bukti || 'Ada'}
                                  </p>
                                )}

                                {/* User info */}
                                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                                  <span>👤 {req.requested_by?.nama}</span>
                                  <span>⏱️ {timeAgo(req.created_at)}</span>
                                </div>

                                {/* Current saldo */}
                                <div className={`mt-3 p-2 rounded text-sm ${getSaldoLoket(req.loket_id, req.bank_code) < 500000 ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                                  💰 Saldo saat ini: {formatRupiah(getSaldoLoket(req.loket_id, req.bank_code))}
                                </div>
                              </div>

                              {/* Action buttons */}
                              <div className="flex flex-col gap-2 ml-4">
                                <button
                                  onClick={() => openApproveModal(req)}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                                >
                                  <CheckCircle size={16} />
                                  Approve
                                </button>
                                <button
                                  onClick={() => openRejectModal(req)}
                                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2"
                                >
                                  <XCircle size={16} />
                                  Reject
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* HISTORY TAB */}
              {activeTab === 'history' && (
                <div>
                  {/* Filters */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    <select
                      value={filterLoket}
                      onChange={(e) => { setFilterLoket(e.target.value); setActiveTab('history'); }}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="">Semua Loket</option>
                      {lokets.map(l => (
                        <option key={l.id} value={l.id}>{l.nama}</option>
                      ))}
                    </select>
                    <select
                      value={filterBank}
                      onChange={(e) => { setFilterBank(e.target.value); loadData(); }}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="">Semua Bank</option>
                      <option value="BRIS">💚 BSI</option>
                      <option value="MANDIRI">💙 Bank Mandiri</option>
                    </select>
                    <select
                      value={filterKategori}
                      onChange={(e) => { setFilterKategori(e.target.value); loadData(); }}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="">Semua Kategori</option>
                      {kategoriOptions.map(k => (
                        <option key={k.value} value={k.value}>{k.icon} {k.label}</option>
                      ))}
                    </select>
                  </div>

                  {filteredHistory.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <History className="mx-auto mb-2 text-gray-400" size={48} />
                      <p>Belum ada riwayat inject saldo</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredHistory.map(item => {
                        const kat = getKategoriInfo(item.kategori);
                        return (
                          <div key={item.id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                {/* Header */}
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${kat.bgColor} ${kat.textColor}`}>
                                    {kat.icon} {kat.label}
                                  </span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    item.source === 'DIRECT' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {item.source === 'DIRECT' ? '🔵 Direct' : '✅ Approved'}
                                  </span>
                                </div>

                                {/* Loket & Bank */}
                                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                  <span className="font-medium">{item.loket_nama}</span>
                                  <span>|</span>
                                  <span>{item.bank_code === 'BRIS' ? '💚 BSI' : '💙 Mandiri'}</span>
                                </div>

                                {/* Amount */}
                                <p className="text-xl font-bold text-green-600">
                                  +{formatRupiah(item.jumlah_inject)}
                                </p>

                                {/* Saldo change */}
                                <p className="text-sm text-gray-500 mt-1">
                                  💰 {formatRupiah(item.saldo_sebelum)} → {formatRupiah(item.saldo_sesudah)}
                                </p>

                                {/* Alasan */}
                                <p className="text-sm text-gray-600 mt-2">{item.alasan}</p>

                                {/* Bukti */}
                                {item.bukti_inject && (
                                  <p className="text-xs text-gray-500 mt-1">📎 Bukti inject tersedia</p>
                                )}

                                {/* User & Time */}
                                <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                                  <span>👤 {item.injected_by?.nama}</span>
                                  <span>📅 {timeAgo(item.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* DIRECT INJECT TAB */}
              {activeTab === 'direct' && (
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Plus className="text-green-600" />
                    Inject Langsung
                  </h2>

                  <form onSubmit={handleDirectInject} className="space-y-4 max-w-2xl">
                    {/* Kategori */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Kategori *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {kategoriOptions.map(kat => (
                          <button
                            key={kat.value}
                            type="button"
                            onClick={() => setDirectInjectForm({ ...directInjectForm, kategori: kat.value })}
                            className={`p-3 border-2 rounded-lg text-left transition-all ${
                              directInjectForm.kategori === kat.value
                                ? `${kat.borderColor} ${kat.bgColor}`
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{kat.icon}</span>
                              <span className="font-medium">{kat.label}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Loket */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pilih Loket *
                      </label>
                      <select
                        value={directInjectForm.loket_id}
                        onChange={(e) => setDirectInjectForm({ ...directInjectForm, loket_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="">-- Pilih Loket --</option>
                        {lokets.map(l => (
                          <option key={l.id} value={l.id}>
                            {l.kode} - {l.nama}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Bank */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bank *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setDirectInjectForm({ ...directInjectForm, bank_code: 'BRIS' })}
                          className={`p-3 border-2 rounded-lg text-center transition-all ${
                            directInjectForm.bank_code === 'BRIS'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-300 hover:border-green-300'
                          }`}
                        >
                          <div className="font-semibold">💚 BSI</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDirectInjectForm({ ...directInjectForm, bank_code: 'MANDIRI' })}
                          className={`p-3 border-2 rounded-lg text-center transition-all ${
                            directInjectForm.bank_code === 'MANDIRI'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-blue-300'
                          }`}
                        >
                          <div className="font-semibold">💙 Bank Mandiri</div>
                        </button>
                      </div>
                    </div>

                    {/* Current Saldo */}
                    {directInjectForm.loket_id && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                          💰 Saldo saat ini: {
                            formatRupiah(
                              lokets.find(l => l.id === directInjectForm.loket_id)
                                ?.banks?.find(b => b.bank_code === directInjectForm.bank_code)
                                ?.saldo || 0
                            )
                          }
                        </p>
                      </div>
                    )}

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Jumlah Inject (Rp) *
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="number"
                          value={directInjectForm.jumlah_inject}
                          onChange={(e) => setDirectInjectForm({ ...directInjectForm, jumlah_inject: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Masukkan jumlah"
                          min="-999999999"
                          required
                        />
                      </div>
                      {directInjectForm.kategori === 'REVISI_SISTEM' || directInjectForm.kategori === 'REVISI_INPUT' ? (
                        <p className="mt-1 text-xs text-blue-600">
                          💡 Untuk revisi, masukkan nominal minus (contoh: -100000)
                        </p>
                      ) : null}
                    </div>

                    {/* Alasan */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Alasan *
                      </label>
                      <textarea
                        value={directInjectForm.alasan}
                        onChange={(e) => setDirectInjectForm({ ...directInjectForm, alasan: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        rows="2"
                        placeholder="Jelaskan alasan inject..."
                        required
                      />
                    </div>

                    {/* Keterangan Tambahan */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Keterangan Tambahan
                      </label>
                      <textarea
                        value={directInjectForm.keterangan_tambahan}
                        onChange={(e) => setDirectInjectForm({ ...directInjectForm, keterangan_tambahan: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        rows="2"
                        placeholder="Keterangan tambahan (opsional)..."
                      />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting || !directInjectForm.kategori || !directInjectForm.loket_id || !directInjectForm.jumlah_inject || !directInjectForm.alasan}
                      className={`w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 ${
                        submitting || !directInjectForm.kategori || !directInjectForm.loket_id || !directInjectForm.jumlah_inject || !directInjectForm.alasan
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {submitting ? (
                        <>
                          <RefreshCw className="animate-spin" size={20} />
                          Memproses...
                        </>
                      ) : (
                        <>
                          <Plus size={20} />
                          INJECT SEKARANG
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-green-600 px-6 py-4 rounded-t-xl sticky top-0">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <CheckCircle size={20} />
                Approve & Inject Saldo
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Request Info */}
              <div className={`p-4 rounded-lg border-2 ${getKategoriInfo(selectedRequest.kategori).borderColor}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${getKategoriInfo(selectedRequest.kategori).bgColor} ${getKategoriInfo(selectedRequest.kategori).textColor}`}>
                    {getKategoriInfo(selectedRequest.kategori).icon} {getKategoriInfo(selectedRequest.kategori).label}
                  </span>
                </div>
                <p className="font-medium">{selectedRequest.loket_nama} | {selectedRequest.bank_code === 'BRIS' ? '💚 BSI' : '💙 Mandiri'}</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {formatRupiah(selectedRequest.jumlah_request)}
                </p>
                <p className="text-sm text-gray-600 mt-2">{selectedRequest.alasan}</p>
                {selectedRequest.bukti && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    📎 Bukti: {selectedRequest.nama_file_bukti || 'Ada'}
                  </p>
                )}
              </div>

              {/* Current Saldo */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  💰 Saldo saat ini: {formatRupiah(getSaldoLoket(selectedRequest.loket_id, selectedRequest.bank_code))}
                </p>
              </div>

              {/* Amount Override */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jumlah Inject (Rp)
                </label>
                <input
                  type="number"
                  value={approveAmount}
                  onChange={(e) => setApproveAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  min="-999999999"
                />
              </div>

              {/* Keterangan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keterangan Manager
                </label>
                <textarea
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="2"
                  placeholder="Inject disetujui..."
                />
              </div>

              {/* Preview */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-700 mb-2">Preview Saldo:</p>
                <p className="text-sm">💰 Sebelum: {formatRupiah(getSaldoLoket(selectedRequest.loket_id, selectedRequest.bank_code))}</p>
                <p className="text-sm">➕ Inject: {formatRupiah(parseFloat(approveAmount || 0))}</p>
                <p className="text-sm font-bold">💰 Sesudah: {formatRupiah(getSaldoLoket(selectedRequest.loket_id, selectedRequest.bank_code) + parseFloat(approveAmount || 0))}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleApprove}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {submitting ? 'Memproses...' : '✓ Approve & Inject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-red-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <XCircle size={20} />
                Tolak Request
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Request Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedRequest.loket_nama} | {selectedRequest.bank_code === 'BRIS' ? '💚 BSI' : '💙 Mandiri'}</p>
                <p className="text-xl font-bold text-purple-600">
                  {formatRupiah(selectedRequest.jumlah_request)}
                </p>
                <p className="text-sm text-gray-600 mt-1">{selectedRequest.alasan}</p>
              </div>

              {/* Rejection Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alasan Penolakan *
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  rows="3"
                  placeholder="Jelaskan alasan penolakan..."
                  required
                />
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-50 rounded-lg flex items-start gap-2">
                <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-yellow-700">
                  Requester akan mendapat notifikasi tentang penolakan ini.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleReject}
                  disabled={submitting || !rejectNote.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {submitting ? 'Memproses...' : '✗ Tolak Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InjectSaldoManager;
